import {
    Container,
    Row,
    Col,
    Breadcrumb,
    Button,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Table,
    InputGroup,
    Form,
    Spinner,
    Nav,
    Alert,
} from "react-bootstrap";
import { useState, useRef, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { FaPlus } from "react-icons/fa";
import PaginationComponent from "../common/Pagination";
import CreateNewClient from './CreateNewClient'
import { CREATE_CLIENT_FIELDS } from "./ClientHelper";
import { getClientsList, getPendingClientsList } from './ClientHelper';
import Base from '../Base';
import { useNavigate } from 'react-router-dom';
import { FiEye } from "react-icons/fi";
import { truncateText } from "../../utils";
import { extractErrorMessage } from "../../constants/common";
import toast from "react-hot-toast";

export default function ClientList() {
    const [activeTab, setActiveTab] = useState("clientList");
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const searchRef = useRef();
    const PER_PAGE = 25;
    const [showAddClient, SetAddClient] = useState(false);
    const [showProcess, setShowProcess] = useState(false);
    const [selectedClientId, setSelectedClientId] = useState(null);
    const navigate = useNavigate();
    const clientFields = CREATE_CLIENT_FIELDS;

    // API data states
    const [clients, setClients] = useState([]);
    const [pendingClients, setPendingClients] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [error, setError] = useState(null);

    // Form states
    const initialFormValues = {}; // Define your initial form values here
    const [formValues, setFormValues] = useState(initialFormValues);
    const [formErrors, setFormErrors] = useState({});

    // Fetch clients data
    const fetchClients = async () => {
        try {
            setLoading(true);
            setError(null);

            let response;
            if (activeTab === "clientList") {
                response = await getClientsList(currentPage, PER_PAGE, searchQuery);
            } else {
                response = await getPendingClientsList(currentPage, PER_PAGE, searchQuery);
            }

            if (response.type === 'success') {
                const clientsData = response.data.lists || [];

                if (activeTab === "clientList") {
                    setClients(clientsData);
                } else {
                    setPendingClients(clientsData);
                }

                setTotalCount(response.data.total_count || 0);
            } else {
                setError('Failed to fetch clients data');
            }
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            toast.error(errorMessage)
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Handle client form submission
    const handleClientSubmit = (e, isUpdate) => {
        e.preventDefault();
        fetchClients();
    };

    // Handle tab selection
    const handleTabSelect = (tabName) => {
        setActiveTab(tabName);
        setCurrentPage(1); // Reset to first page when changing tabs
    };

    // Handle search
    const handleSearch = () => {
        const searchText = searchRef.current.value.trim();
        setSearchQuery(searchText);
        setCurrentPage(1); // Reset to first page when searching
    };

    // Handle pagination
    const handlePagination = (page) => {
        setCurrentPage(page);
    };

    // Handle add client click
    const addClientClick = () => {
        SetAddClient(true);
    };

    // Get current clients based on active tab
    const getCurrentClients = () => {
        return activeTab === "clientList" ? clients : pendingClients;
    };

    // Fetch data on component mount and when dependencies change
    useEffect(() => {
        fetchClients();
    }, [activeTab, currentPage, searchQuery]);

    return (
        <Base>
            <Container>
                <Row>
                    <Col>
                        <Breadcrumb>
                            <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: "/" }}>
                                Dashboard
                            </Breadcrumb.Item>
                            <Breadcrumb.Item active>Clients</Breadcrumb.Item>
                        </Breadcrumb>
                    </Col>
                </Row>

                <Row>
                    <Col sm={12}>
                        <div className="d-flex justify-content-between align-items-center">
                            <h2 className="fs-3 fw-semibold">Client Management</h2>
                            <Button
                                variant="primary d-flex align-items-center fw-semibold btn btn-primary"
                                onClick={addClientClick}
                            >
                                <FaPlus className="me-2" />
                                Add New Client
                            </Button>
                        </div>

                        <Card className="border-0">
                            <CardHeader>
                                <div className="d-flex justify-content-between align-items-center">
                                    <Nav variant="underline" activeKey={activeTab} onSelect={handleTabSelect}>
                                        <Nav.Item>
                                            <Nav.Link eventKey="clientList" className="text-primary">
                                                Client List
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link eventKey="pendingClients" className="text-primary">
                                                Pending Clients
                                            </Nav.Link>
                                        </Nav.Item>
                                    </Nav>

                                    <InputGroup className="w-auto">
                                        <Form.Control
                                            placeholder="Search client name..."
                                            ref={searchRef}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleSearch();
                                                }
                                            }}
                                        />
                                        <Button variant="outline-secondary" onClick={handleSearch}>
                                            <i className="ri-search-line"></i>
                                        </Button>
                                    </InputGroup>
                                </div>
                            </CardHeader>

                            <CardBody>
                                {loading ? (
                                    <div className="d-flex justify-content-center py-4">
                                        <Spinner animation="border" />
                                    </div>
                                ) : (
                                    <Table responsive bordered className="core-ui-table">
                                        <thead>
                                            <tr>
                                                <th className="text-left">Client Name</th>
                                                <th className="text-left">Phone</th>
                                                <th className="text-left">Email</th>
                                                <th className="text-left">Category</th>
                                                <th className="text-center">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getCurrentClients().length > 0 ? (
                                                getCurrentClients().map((client) => (
                                                    <tr key={client.id}>
                                                        <td className="text-left">{truncateText(client.client_name, 30)}</td>
                                                        <td className="text-left">{client.phone || "-"}</td>
                                                        <td className="text-left">{truncateText(client.email, 30)}</td>
                                                        <td className="text-left">
                                                            {client?.categories && client.categories?.name
                                                                ? truncateText(client.categories?.name, 30)
                                                                : "-"}
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="d-flex gap-2 text-primary justify-content-center">
                                                                {activeTab === "clientList" && (
                                                                    <>
                                                                        <Button
                                                                            variant="link"
                                                                            title="View Employee"
                                                                            onClick={() => navigate(`/client-process/${client.id}`)}
                                                                        >
                                                                            <FiEye size={18} />
                                                                        </Button>
                                                                    </>
                                                                )}

                                                                {activeTab === "pendingClients" && (
                                                                    <>
                                                                        <Button
                                                                            variant="outline-primary"
                                                                            className="no-hover fw-bold btn-sm"
                                                                            size="sm"
                                                                            onClick={() => navigate(`/client-process/${client.id}`)}
                                                                        >
                                                                            Process
                                                                        </Button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-4">
                                                        <div className="text-muted">
                                                            <p className="mb-0">
                                                                {searchQuery ? 'No clients found matching your search.' : 'No clients found.'}
                                                            </p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                )}
                            </CardBody>

                            <CardFooter className="border-0">
                                <div className="d-flex justify-content-center">
                                    {totalCount > PER_PAGE && (
                                        <PaginationComponent
                                            total_count={totalCount}
                                            current_page={currentPage}
                                            handleOnClick={handlePagination}
                                            per_page={PER_PAGE}
                                        />
                                    )}
                                </div>
                            </CardFooter>
                        </Card>
                    </Col>
                </Row>

                <CreateNewClient
                    show={showAddClient}
                    onHideFunc={() => SetAddClient(false)}
                    fields={clientFields}
                    errors={formErrors}
                    values={formValues}
                    handleSubmit={handleClientSubmit}
                    editClientPermission={true}
                />
            </Container>
        </Base>
    );
}